'use client'

import { Box, Grid, Table, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { useExchangeRates } from '@/services/currency'
import { CoinOverall } from '@/types/coin'
import { AllAvailableCurrencyCodes } from '@/types/currency'
import { currencySymbolMap } from '@/utils/number/currencyFormatter'
import { formatEffectiveNumber } from '@/utils/number/format'

const UNITS = [0.5, 1, 5, 10, 50, 100, 500, 1000]

interface ConversionTableProps {
  currency: AllAvailableCurrencyCodes
  coin: CoinOverall
}

const TableSection = ({
  title,
  rows,
  lastUpdate,
}: {
  title: string
  rows: { label: string; value: string }[]
  lastUpdate: string
}) => (
  <Box>
    <Box>
      <Text fz={16} fw={600} lh={'20px'}>
        {title}
      </Text>
      <Text fz={14} fw={500} lh={'20px'} c="var(--content-secondary)">
        {lastUpdate}
      </Text>
    </Box>
    <Table verticalSpacing={12} horizontalSpacing={24} withRowBorders layout="fixed">
      <Table.Tbody>
        {rows.map((row, index) => (
          <Table.Tr key={index}>
            <Table.Td fz={14} fw={600}>
              {row.label}
            </Table.Td>
            <Table.Td
              fz={14}
              fw={600}
              style={{
                textAlign: 'right',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: '60%',
              }}
            >
              {row.value}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Box>
)

function ConversionTable({ currency, coin }: ConversionTableProps) {
  const t = useTranslations('converter')
  const { data: exchangeRates } = useExchangeRates()

  const fiatRate = useMemo(() => {
    if (!exchangeRates) return 0
    const rate = exchangeRates[currency]
    return rate ? Number(rate) : 0
  }, [exchangeRates, currency])

  const tokenPriceInFiat = (coin.usd_price || 0) * fiatRate
  const currencySymbol = currencySymbolMap[currency] || ''

  const tokenToFiatRows = UNITS.map(unit => {
    const value = tokenPriceInFiat * unit
    return {
      label: `${unit.toLocaleString()} ${coin.symbol}`,
      value:
        tokenPriceInFiat > 0
          ? `${currencySymbol}${formatEffectiveNumber(value)} ${!currencySymbol ? currency : ''}  `
          : '-',
    }
  })

  const fiatToTokenRows = UNITS.map(unit => {
    const value = tokenPriceInFiat > 0 ? unit / tokenPriceInFiat : 0
    return {
      label: `${unit.toLocaleString()} ${currency}`,
      value: value > 0 ? `${formatEffectiveNumber(value)} ${coin.symbol}` : '-',
    }
  })

  const lastUpdate = useMemo(() => {
    if (!exchangeRates) return ''
    return new Date(exchangeRates.lastUpdate).toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }, [exchangeRates])

  return (
    <Box>
      <Text component="h2" fz={20} lh={'28px'} fw={600} mb={16}>
        {t('tokenConversionTables', { token: coin.name })}
      </Text>

      <Grid gutter={{ base: 40, md: 80, lg: 100 }}>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TableSection
            title={t('tokenToFiat', { token: coin.symbol, fiat: currency })}
            rows={tokenToFiatRows}
            lastUpdate={t('asOfDate', { date: lastUpdate })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TableSection
            title={t('fiatToToken', { fiat: currency, token: coin.symbol })}
            rows={fiatToTokenRows}
            lastUpdate={t('asOfDate', { date: lastUpdate })}
          />
        </Grid.Col>
      </Grid>
    </Box>
  )
}

export default SectionErrorBoundary.with(ConversionTable)
